import classNames from "classnames";
import { FC, useContext, useEffect, useState } from "react";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { TabsPanel } from "../../../components/tabs";
import styles from "../../styles/index.module.scss";
import tabsStyles from "../../../pages/styles/homePage.module.scss";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../system/providers/plugin";
import useGridFilter from "../../../system/hooks/useGridFilter";
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import { ChequeDataProvider } from "../../../Services/DataProviders/ChequeDataProvider";
import { IChequeViewDTO } from "../../../libs/coreapi-dto/accounting/cheque";
import { ChequePanel } from "./Components/ChequePanel";
import { DatePeriodPanel } from "../../../hoc/DatePeriodPanel/DatePeriodPanel";
import { DateTime } from "luxon";
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker";
import { DocumentGridStateType, ISaveFocus } from "../../../@types/documents";
import { IKeyValuePair } from "../../Reports/Shared/Interfaces/IKeyValuePair";
import { DocumentType, IdTableVariant, LoadingStatus } from "../../../@types/enumsGlobal";
import { TextInput } from "../../../components/controls/inputs";
import { IOption, Select } from "../../../components/selects/select";
import { Spinner } from "../../../components/spiner/Spinner";
import FlexRow from "../../../components/controls/FlexRow";
import { KizDocDataProvider } from "../../../Services/DataProviders/KizDocDataProvider";
import SearchModalWindowAntd from "../../../components/modalWindows/SearchModalWindowAntd/SearchModalWindowAntd";
import { PaymentTypeModal } from "./Components/ChangePaymentTypeModal/PaymentTypeModal";

const DefaultGridFilter = (datePeriod) => {
    return {
        numberPerPage: 10,
        pageNumber: 1,
        fieldOrderBy: "documentDate",
        orderDesc: true,
        columnFilters: [
            ...(datePeriod?.startDate
                ? [
                      {
                          name: "documentDate",
                          value: datePeriod.startDate.toFormat("yyyyMMdd"),
                          operator: "MoreOrEq",
                          invisible: true,
                      } as IGridColumnFilter,
                  ]
                : []),
            ...(datePeriod?.endDate
                ? [
                      {
                          name: "documentDate",
                          value: datePeriod.endDate.toFormat("yyyyMMdd"),
                          operator: "LessOrEq",
                          invisible: true,
                      } as IGridColumnFilter,
                  ]
                : []),
        ],
    };
};

export const ChequeGridView: FC<IGridProps> = (props) => {
    const appContext = useAppContext();
    const cdp = new ChequeDataProvider(appContext.coreApiService);
    const kizDocDP = new KizDocDataProvider(appContext.coreApiService);
    const pluginCtx = usePluginContext();
    const DefaultDatePeriod = { startDate: DateTime.now().plus({ days: -7 }), endDate: DateTime.now() } as IDatePeriod;
    const [datePeriod, setDatePeriod] = useState<IDatePeriod>(DefaultDatePeriod);
    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultGridFilter(DefaultDatePeriod));
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [chequeSelected, setChequeSelected] = useState<IChequeViewDTO>();
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);
    const [showModalChangePaymentType, setShowModalChangePaymentType] = useState<boolean>(false);
    const [viewState, setViewState] = useState<DocumentGridStateType>("view");
    const [_, setModalSearch] = useState(<></>)
    const defaultSearchOption = {displayName: 'Позиция в чеке', value: 'lotName'} as IOption;
    const [searchOption, setSearchOption] = useState<IOption>(defaultSearchOption);
    const [searchValue, setSearchValue] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);

    const [totalCount, setTotalCount] = useState<number>(0);
    const [data, setData] = useState<IChequeViewDTO[]>([]);

    const [saveFocus, setSaveFocus] = useState<ISaveFocus>({
        viewState: 'view',
        selectedItem: selectedItem,
        data: data
    });
    const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);

    const handleSearchToggle = () => {
        setIsSearchModalVisible(!isSearchModalVisible);
        setViewState(isSearchModalVisible ? "return" : "search");
    };

    const hasActiveFilters = () => {
        return (gridFilter?.columnFilters || []).some(filter => 
            !(filter.name === "documentDate" && 
              (filter.operator === "MoreOrEq" || filter.operator === "LessOrEq"))
        );
    };

    useEffect(() => {
        setIsSubmitting(true);
        cdp.getViewParam(gridFilter, (entities, totalCount) => {
            setTotalCount(totalCount);
            setData(entities);
            setIsSubmitting(false);
        });
    }, [gridFilter,viewState]);

    useEffect(() => {
        switch (viewState) {
            case "refresh" : refresh(); break;            
            default: setModalSearch(<></>);
        }
    }, [viewState]);

    useEffect(() => {
        if (selectedItem) {
            setChequeSelected(data.find((x) => x.idGlobal === selectedItem.idGlobal) as IChequeViewDTO);
        } else {
            setChequeSelected(undefined);
        }
    }, [selectedItem]);

    useEffect(() => {
        if (saveFocus.selectedItem)
            setChequeSelected(saveFocus.selectedItem as any);
    }, [saveFocus.selectedItem]);

    useEffect(() => {
        if (searchOption.value === 'lotName') {
            dispatchGridFilter({ type: "deleteColumnFilter", payload: "RecipeSeriaNumber" });
        } else if (searchOption.value === 'recipeSeriaNumber') {
            dispatchGridFilter({ type: "deleteParamFilter" })
        }

        //quickSearch(searchValue)
    }, [searchOption])

    function refresh() {
        cdp.getViewParam(gridFilter, (entities, totalCount) => {
            setTotalCount(totalCount);
            setData(entities);
        });
        setViewState("view");
    }
    
    const quickSearch = (value: string) => {
        if (searchOption.value === 'lotName') {
            dispatchGridFilter({
                type: 'paramSimpleFilter',
                payload: {gridParamFilter: {lotName: value}, gridColumnFilter: gridFilter.columnFilters},
            });
        } else if (searchOption.value === 'recipeSeriaNumber') {
            dispatchGridFilter({
                type: 'addColumnFilter',
                payload: {
                    name: "RecipeSeriaNumber",
                    value: `%${value.replace('\\', "\\\\")}%`,
                    operator: "Like"
                },
            });
        }
    };

    return (
        <>
            <PluginWrapper>
                <ToolbarWrapper>
                    <FlexRow wrap>
                        <ChequePanel
                            process={{
                                onClick: () => {
                                    cdp.process(selectedItem?.idGlobal as string, () => {
                                        setSaveFocus({...saveFocus, viewState: 'process'});
                                    });
                                },
                                disabled: selectedItem && chequeSelected?.documentState !== "proc" && chequeSelected?.documentState !== "del" ? false : true,
                            }}
                            unProcess={{
                                onClick: () => {
                                    cdp.unProcess(selectedItem?.idGlobal as string, () => {
                                        setSaveFocus({...saveFocus, viewState: 'unprocess'});
                                    });
                                },
                                disabled: selectedItem && chequeSelected?.documentState !== "draft" && chequeSelected?.documentState !== "del" ? false : true,
                            }}
                            delete={{
                                onClick: () => {
                                    cdp.markDelete(selectedItem?.idGlobal as string, (e) => {
                                        if (e.respType != 'isFailed') {
                                            setSaveFocus({...saveFocus, viewState: 'delete'});
                                        }
                                    });
                                },
                                disabled: chequeSelected === undefined || chequeSelected?.documentState === "del" || chequeSelected?.documentState === "proc"
                            }}
                            restore={
                                {
                                    onClick: () => {
                                        cdp.restore(selectedItem?.idGlobal as string, (e) => {
                                            if (e.respType != 'isFailed') {
                                                setSaveFocus({...saveFocus, viewState: 'restore'});
                                            }
                                        })
                                    },
                                     disabled:
                                     (chequeSelected && chequeSelected?.documentState !== "del") ?? true,
                                }
                            }
                            print={{
                                onClick: () => {
                                    return IdTableVariant.Cheque;
                                },
                                onSubItemClick: () => {
                                    let arr = new Array<IKeyValuePair>();
                                    let rows: Array<IGridRow> = new Array<IGridRow>();
                                    rows.push(selectedItem as IGridRow); //ToDo siv здесь нужно поставлять все выделенные строки, но не понятно как
                                    rows.forEach(function (row) {
                                        const mnemocode = row.cells.find((e) => {
                                            return e.propertyName == "mnemocode";
                                        })?.value;
                                        if (mnemocode != undefined) arr.push({ key: row.idGlobal, value: mnemocode });
                                    });
                                    return arr;
                                },
                                disabled: selectedItem && chequeSelected?.documentState !== "del" ? false : true,
                            }}
                            refresh={{ onClick: () => setViewState("refresh") }}
                            hasActiveFilters={hasActiveFilters()}
                            search={{ 
                                onClick: handleSearchToggle,
                                disabled: false
                            }}
                            permission={props.plugin.permission}
                            items={[
                                {
                                    label: "Отправить 521 схему",
                                    onClick: () => {
                                        kizDocDP.create521ChequeDocument(selectedItem?.idGlobal as string);
                                    },
                                    disabled: selectedItem?.cells.find(element => element.propertyName === 'recipeSeriaNumber')?.value ? false : true
                                },
                                {
                                    label: "Изменить тип оплаты",
                                    onClick: () => setShowModalChangePaymentType(true)
                                }
                            ]}
                        />
                        <TextInput
                            disabled={false}
                            label={''}
                            inputId={'inputSearch'}
                            inline={true}
                            searchButtonShow={true}
                            value={searchValue}
                            onChange={(value) => {
                                setSearchValue(value);
                                setSelectedItem(undefined);
                            }}
                            onEndChange={(value) => {
                                setSearchValue(value);
                                setSelectedItem(undefined);
                                quickSearch(value);
                            }}
                            searchButtonOnClick={() => {
                                //setViewState('refresh');
                                setSelectedItem(undefined);
                                quickSearch(searchValue);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { 
                                    //setViewState('refresh');
                                    setSelectedItem(undefined);
                                    quickSearch(searchValue);
                                }
                            }}
                        />
                        <Select
                            className={styles.filterBy}
                            defaultOption={defaultSearchOption}
                            onSelect={(option) => {
                                setSearchOption(option);
                                setSelectedItem(undefined);
                            }}
                            value={searchOption}
                            options={[defaultSearchOption, {displayName: 'Серия\\номер рецепта', value: 'recipeSeriaNumber'} as IOption]}
                        />
                        <DatePeriodPanel
                            datePeriod={datePeriod}
                            onDatePeriodChange={(date: IDatePeriod) => {
                                setSelectedItem(undefined);
                                setDatePeriod(date);
                                dispatchGridFilter({
                                    type: "sortByFilters",
                                    payload: {
                                        gridColumnFilter: [
                                            ...(date?.startDate
                                                ? [
                                                    {
                                                        name: "documentDate",
                                                        value: date.startDate.toFormat("yyyyMMdd"),
                                                        operator: "MoreOrEq",
                                                    } as IGridColumnFilter,
                                                ]
                                                : []),
                                            ...(date?.endDate
                                                ? [
                                                    {
                                                        name: "documentDate",
                                                        value: date.endDate.toFormat("yyyyMMdd"),
                                                        operator: "LessOrEq",
                                                    } as IGridColumnFilter,
                                                ]
                                                : []),
                                        ],
                                    },
                                });
                            }}
                        />
                    </FlexRow>
                </ToolbarWrapper>

                <div className={styles.gridWrapper}>
                    <div className={styles.masterGrid}>
                        {isSubmitting ? (
                            <Spinner />
                        ) :
                            <DefaultGrid
                                showQuantity
                                gridId={props.gridId}
                                data={data}
                                filter={gridFilter}
                                totalCount={totalCount}
                                dataProvider={cdp}
                                plugin={props.plugin}
                                selectedItem={selectedItem}
                                saveFocus={[saveFocus, setSaveFocus]}
                                hiddenPagination={undefined}
                                separator
                                searching
                                documentStatus
                                getView={(gridFilter,callback) => {
                                    cdp.getViewParam(gridFilter, (entities) => {
                                        callback(entities)
                                        setSelectedItem(undefined);
                                    })
                                }}
                                onSelect={(row) => {
                                    setSelectedItem(row);
                                    row ? pluginCtx.masterGrid.onSelectEvent(row, DocumentType.cheque) : pluginCtx.masterGrid.onUnselectEvent();
                                }}
                                onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                                onFilterDelete={(i) => {
                                    i.propertyName === "documentDate" && setDatePeriod({ startDate: null, endDate: null });
                                    dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName });
                                }}
                                onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                                onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                            />
                        }
                        {isSearchModalVisible && (
                            <SearchModalWindowAntd
                                columns={props.plugin?.columns.filter((x) => x.visibility === true) ?? []}
                                defaultFilters={gridFilter.columnFilters}
                                search={(columnFilters) => {
                                    dispatchGridFilter({ type: 'search', payload: columnFilters });
                                    handleSearchToggle();
                                    setViewState("refresh");
                                }}
                                cancel={handleSearchToggle}
                                open={true}
                                onCancel={handleSearchToggle}
                            />
                        )}
                    </div>
                    {
                        <div className={styles.detailsTabView}>
                            {selectedItem && detailsTabsPanel.tabs.length > 0 && (
                                <>
                                    <div className={tabsStyles.tabsPanelWrapper}>
                                        <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: "hidden", width: "100%" }}>
                                            <TabsPanel
                                                id="detailsTabsPanel"
                                                activeId={detailsTabsPanel.currentTab?.id}
                                                tabs={detailsTabsPanel.tabs}
                                                onActive={(id) => {
                                                    dispatchDetailsTabsPanel({ type: "activate", payload: id });
                                                }}
                                            ></TabsPanel>
                                        </div>
                                    </div>
                                    <div className={tabsStyles.contentWrapper}>
                                        {detailsTabsPanel.tabs.map((item) => {
                                            return (
                                                <div key={item.id} className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                                    {item.view.content}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    }
                    {
                        showModalChangePaymentType && selectedItem &&
                        <PaymentTypeModal 
                            idDocumentGlobal={selectedItem.idGlobal}
                            docNum={selectedItem.displayName}
                            cancel={() => setShowModalChangePaymentType(false)} 
                            ok={(value) => {
                                if (selectedItem.idGlobal) {
                                    cdp.updatePaymentTypeAsync(selectedItem.idGlobal as string, value, () => {
                                        refresh()
                                        setShowModalChangePaymentType(false)
                                    })
                                }
                            }} 
                        />
                    }
                </div>
            </PluginWrapper>            
        </>
    );
};
